import type { INode } from './backend/IRenderer'

type EventKey = string
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EventCallback = (e?: any) => void

/**
 * 이벤트를 걸 노드 = 렌더러 핸들(INode, 콜백은 IPointerEvent). 엔티티는 `entity.node`를 넘긴다.
 * 식별은 `node.id() + '_' + key`(무ID 노드는 같은 key끼리 맵에서 덮어쓰임 — 동작 보존,
 * 엔티티 루트 노드 id는 setId가 엔티티 id와 같게 유지), removeAllEvents·removeEventsByPrefix는 참조 비교(핸들은 노드당 1개).
 */
type NodeType = INode

export interface EventItem {
  node: NodeType
  key: EventKey
  callback: EventCallback
}

/**
 * 캔버스 이벤트를 관리하는 클래스
 * - 이벤트 큐 배열로 이벤트를 관리하여 효율적인 이벤트 처리 가능
 * - 이벤트 추가, 제거, 교체, 일괄 제거 등 기능 제공
 */
export class EventManager {
  private eventQueue: Map<string, EventItem> = new Map()
  private static instance: EventManager

  private constructor() {}

  /**
   * 싱글톤 패턴으로 인스턴스 반환
   * @returns EventManager 인스턴스
   */
  public static getInstance(): EventManager {
    if (!EventManager.instance) {
      EventManager.instance = new EventManager()
    }
    return EventManager.instance
  }

  /**
   * 고유 이벤트 ID 생성
   * @param node 이벤트를 추가할 노드
   * @param key 이벤트 키
   * @returns 고유 이벤트 ID
   */
  private generateEventId(node: NodeType, key: EventKey): string {
    return `${node.id()}_${key}`
  }

  /**
   * 이벤트 추가
   * @param node 이벤트를 추가할 노드
   * @param key 이벤트 키
   * @param callback 이벤트 콜백 함수
   */
  public addEvent(node: NodeType, key: EventKey, callback: EventCallback): void {
    const eventId = this.generateEventId(node, key)

    // 이미 해당 이벤트가 등록되어 있으면 제거 후 새로 추가
    if (this.eventQueue.has(eventId)) {
      this.removeEvent(node, key)
    }

    // Konva.js 이벤트 등록시에 내부 함수가 바로 실행되는 경우가 있음
    // 이벤트 등록 시 즉시 실행되지 않도록 setTimeout으로 감싸서 등록
    // (지연 등록 보존 — 모드 전환 직후 첫 스트로크 누락의 원인 후보지만 동작은 바꾸지 않았다)
    setTimeout(() => {
      node.on(key, callback)
      this.eventQueue.set(eventId, { node, key, callback })
    }, 0)
  }

  /**
   * 이벤트 제거
   * @param node 이벤트를 제거할 노드
   * @param key 이벤트 키
   */
  public removeEvent(node: NodeType, key: EventKey): void {
    const eventId = this.generateEventId(node, key)

    if (this.eventQueue.has(eventId)) {
      node.off(key)
      this.eventQueue.delete(eventId)
    }
  }

  /**
   * 이벤트 교체
   * @param node 이벤트를 교체할 노드
   * @param key 이벤트 키
   * @param callback 새로운 이벤트 콜백 함수
   */
  public replaceEvent(node: NodeType, key: EventKey, callback: EventCallback): void {
    this.removeEvent(node, key)
    this.addEvent(node, key, callback)
  }
  /**
   * 특정 노드의 모든 이벤트를 강력하게 제거
   * @param node 이벤트를 제거할 노드
   */
  public removeAllEvents(node: NodeType): void {
    this.eventQueue.forEach((event: EventItem, eventId: string) => {
      if (event.node === node) {
        node.off(event.key)
        this.eventQueue.delete(eventId)
      }
    })
  }

  /**
   * 특정 노드의 특정 접두사를 가진 모든 이벤트 제거
   * @param node 이벤트를 제거할 노드
   * @param prefix 이벤트 키 접두사
   */
  public removeEventsByPrefix(node: NodeType, prefix: string): void {
    const eventsToRemove: string[] = []

    for (const [eventId, event] of this.eventQueue.entries()) {
      if (event.node === node && event.key.startsWith(prefix)) {
        eventsToRemove.push(eventId)
      }
    }

    for (const eventId of eventsToRemove) {
      const event = this.eventQueue.get(eventId)
      if (event) {
        node.off(event.key)
        this.eventQueue.delete(eventId)
      }
    }
  }

  /**
   * 여러 이벤트 키를 가진 이벤트를 한 번에 추가
   * @param node 이벤트를 추가할 노드
   * @param keys 이벤트 키 배열
   * @param callback 이벤트 콜백 함수
   */
  public addEvents(node: NodeType, keys: EventKey[], callback: EventCallback): void {
    for (const key of keys) {
      this.addEvent(node, key, callback)
    }
  }

  /**
   * 여러 이벤트 키를 가진 이벤트를 한 번에 제거
   * @param node 이벤트를 제거할 노드
   * @param keys 이벤트 키 배열
   */
  public removeEvents(node: NodeType, keys: EventKey[]): void {
    for (const key of keys) {
      this.removeEvent(node, key)
    }
  }

  /**
   * 이벤트 스트링으로 이벤트 추가
   * @param node 이벤트를 추가할 노드
   * @param eventString 이벤트 스트링 (예: 'click.default tap.default')
   * @param callback 이벤트 콜백 함수
   */
  public addEventFromString(node: NodeType, eventString: string, callback: EventCallback): void {
    const events = eventString.split(' ')
    for (const event of events) {
      if (event.trim() !== '') {
        this.addEvent(node, event, callback)
      }
    }
  }

  /**
   * 이벤트 스트링으로 이벤트 제거
   * @param node 이벤트를 제거할 노드
   * @param eventString 이벤트 스트링 (예: 'click.default tap.default')
   */
  public removeEventFromString(node: NodeType, eventString: string): void {
    const events = eventString.split(' ')
    for (const event of events) {
      if (event.trim() !== '') {
        this.removeEvent(node, event)
      }
    }
  }

  /**
   * 이벤트 큐에 있는 모든 이벤트 조회
   * @returns 모든 이벤트 아이템 배열
   */
  public getAllEvents(): EventItem[] {
    return Array.from(this.eventQueue.values())
  }
}
